// @flow
import * as React from "react"
import { Redirect } from "react-router-dom"
import { sum, groupWith, equals, path } from "ramda"
import { compose, withProps, type HOC } from "recompose"
import withSubmit from "hoc/withSubmit"
import withUser from "hoc/withUser"
import withMutation from "hoc/withMutation"
import withQuery from "hoc/withQuery"
import withConnect from "hoc/withConnect"
import featureFlags from "global/feature-flags"
import { trackReservation } from "analytics/reservations/analyticsTraits"
import ReservationSuccess from "modals/ReservationSuccess"
import { reservationListQuery } from "data/reservations/graphql/queries"
import { createReservationMutation } from "data/reservations/graphql/mutations"
import { selectAppConfig } from "data/app/selectors"
import { selectDates, selectOriginalReservation, selectAccessories, selectShippingAddress } from "data/reservations/selectors"
import type { ProductVariant, ProductVariantAvailableAffiliate } from "data/product/types"
import type { PendingReservation } from "data/basket/types"
import { reservationStatuses } from "data/reservations/constants"
import { clear } from "data/search/actions"
import { addToBasket } from "data/basket/actions"
import { getPricePerDayArray } from "data/units/money/helpers"
import urls from "data/router/urls"
import { openModal } from "data/modals/actions"
import getErrorMessage from "common/graphql/getErrorMessage"
import ProductPreview from "components/ProductPreview"
import ReservationPricing from "components/ReservationPricing"
import SubscriptionUpsell from "components/SubscriptionUpsell"
import StyledLink from "components/StyledLink"
import { getPlan } from "data/plans/helpers"
import { Wrap, WrapGrouper } from "componentsStyled/Layout/Wrap"
import { Note } from "componentsStyled/Typography/Other"
import { Text } from "componentsStyled/Typography/Texts"
import { VPaddedFullWidthContainer } from "componentsStyled/Layout/Containers"
import PromoCodeLink from "./PromoCodeLink"
import Dates from "./Dates"
import ReservationActions from "./ReservationActions"
import CreditCard from "modals/CreditCard"

const hasPreviousReservation = data => data &&
  Object.keys(reservationStatuses).some(status =>
    data[status] && data[status].length
  )

const CheckoutForm = ({
  appConfig,
  productVariant,
  dates,
  variantAffiliate,
  submit,
  loading,
  user,
  originalReservation,
  accessories, // The list of chosen accessories by the user
  shippingAddress,
  openModal,
}) => {
  if (!dates) {
    return <Redirect to={urls.products} />
  }

  const showUpsell = featureFlags.showSubscriptionUpsellInProductsPage()

  const pricingInput = {
    productVariantId: productVariant.id,
    affiliateId: variantAffiliate.affiliateId,
    start: dates.startDate,
    end: dates.endDate,
    accessories: accessories && accessories.map(a => ({ name: a.name })),
  }

  const availability = {
    product: productVariant,
    user: user,
    selectedDates: {
      startDate: dates.startDate,
      endDate: dates.endDate,
    },
    affiliate: variantAffiliate.name,
  }

  const productGroup = productVariant.product.productGroup

  const userSubscriptionSlug = path(["subscription", "plan", "type", "slug"], user)
  const perksPlan = getPlan("Perks")

  const tenantTitle = appConfig ? appConfig.tenantTitle : ""

  return (
    <VPaddedFullWidthContainer narrow>
      <WrapGrouper>
        <Text uppercase bold black>
          Booking details
        </Text>
        <Wrap>
          <Dates dates={dates} originalReservation={originalReservation} />
        </Wrap>
        <Wrap>
          <ProductPreview
            productVariant={productVariant}
            affiliateName={variantAffiliate.name}
          />
        </Wrap>
        <PromoCodeLink
          input={pricingInput}
          originalReservation={originalReservation}
        />
      </WrapGrouper>
      <WrapGrouper>
        <Text uppercase bold black>
          Total
        </Text>
        <Wrap>
          <ReservationPricing
            input={pricingInput}
            originalReservation={originalReservation}
            availability={availability}
            productGroupName={productGroup.name}
            user={user}
            summary={true}
          />
        </Wrap>
        {
        (
          // Reservations made prior to subscription cannot have discounts
          // retrospectively applied or applied only to the new portion so we
          // don't want to offer it here
          showUpsell && !originalReservation &&
          perksPlan && userSubscriptionSlug !== perksPlan.slug)
          ? <SubscriptionUpsell
              input={pricingInput}
              subscriptionPlan={perksPlan}
            />
          : ""
        }
      </WrapGrouper>
      {shippingAddress &&
        <WrapGrouper>
          <Text uppercase bold black>
            Shipping Address
          </Text>
          <Text>
            {shippingAddress.split("\n").map((line) => {
              return <React.Fragment>
                {line}<br />
              </React.Fragment>
            })}
          </Text>
        </WrapGrouper>
      }
      <WrapGrouper>
        <Note>
          You won’t be charged if you cancel your reservation{" "}
          <Text inline book small>
            up to {user.subscribed ? 24 : 48} hours
          </Text>{" "}
          in advance.
        </Note>
      </WrapGrouper>
      <WrapGrouper>
        <Text>
          By proceeding you confirm you accept the following Disclaimer,
          Waiver and Release and the Terms and Conditions available
          {" "}<StyledLink to={urls.terms}>here</StyledLink>.
        </Text>
        <Text>
          You are responsible for any losses, costs, liabilities and expenses
          relating to or arising out of your use of, or inability to use the
          Products during and after your booking.
        </Text>
        <Text>
          The service and/or products are provided on an “as is” basis, with
          all faults. You are responsible for the care of the product while
          in your possession. Products are checked out in proper working
          condition and must be returned in the same condition otherwise the
          store may charge additional amounts for damage or replacement.
        </Text>
        <Text>
          Your use of the service and/or products is at your sole risk. {tenantTitle}, Awayco,
          and their representatives are not
          responsible for any liability, costs and expenses or injury,
          illness, death or loss of or damage to property that may be
          suffered while renting and/or using the products and participating
          in any and all related activities.
        </Text>
        <Text>
          {tenantTitle}, Awayco, and their respective partners, affiliates,
          executives and Directors, explicitly disclaim liability for any
          loss or damage to products and accept no liability for injury or
          damage arising from, during or incidental to a booking. In the
          event that theft, loss or damage is suspected, {tenantTitle} reserves
          discretion to charge the current or last customer, whether
          or not the products were insured, whether or not the damage was
          intentional and whether or not the products were returned. These
          charges will represent a fair value or replacement cost giving
          account of the product and the extent of damage caused, up to the
          recommended retail price for the product/s in the event of theft.
          Charges will be processed using the Awayco booking software and
          against the customer's credit card on file. If customers feel that
          they have been charged unfairly, they may email {tenantTitle} or
          Awayco (support@awayco.com) with further information about their
          situation.
        </Text>
      </WrapGrouper>
      <ReservationActions
        originalReservation={originalReservation}
        addToBasket={appConfig ? appConfig.enableBasket : false}
        loading={loading}
        submit={submit}
      />
    </VPaddedFullWidthContainer>
  )
}

const mapStateToProps = state => ({
  appConfig: selectAppConfig(state),
  dates: selectDates(state),
  originalReservation: selectOriginalReservation(state),
  accessories: selectAccessories(state),
  shippingAddress: selectShippingAddress(state),
})

type Outter = {|
  productVariant: ProductVariant,
  variantAffiliate: ProductVariantAvailableAffiliate,
|}

const mapDispatchToProps = {
  openModal,
  clear,
  addToBasket,
}

const enhancer: HOC<*, Outter> = compose(
  withUser(),
  withConnect(mapStateToProps, mapDispatchToProps),
  withMutation(createReservationMutation),
  withQuery(reservationListQuery, {
    noEmpty: true,
  }),
  withProps(({ dates, variantAffiliate }) => {
    const prices = getPricePerDayArray(dates, variantAffiliate)
    return {
      priceGroups: groupWith(equals, prices),
      total: sum(prices),
    }
  }),
  withSubmit({
    submit: props => () => {

      // curried up reserve product action to deal with modal cb hell.
      // needs to be called only after we have a users CC (if we don't have it yet)
      // if we do have a users CC, we call this action and expect a reservation to be
      // created
      const reserveProduct = async () => {
        // shouldn't ever happen
        if (!props.dates) {
          throw new Error("Impossible")
        }

        const pendingReservation: PendingReservation = {
          start: props.dates.startDate,
          end: props.dates.endDate,
          affiliate: props.variantAffiliate,
          productVariant: props.productVariant,
          paymentMethod: "card",
          accessories: props.accessories.map(a => ({ name: a.name })),
          shippingAddress: props.shippingAddress,
          // Always use the singleton profile
          // Removal of profiles was done in https://app.clubhouse.io/awayco/story/1238/user-app-ui-changes
          profileId: props.user.profiles[0].id,
        }

        if (props.appConfig && props.appConfig.enableBasket) {
          props.addToBasket(pendingReservation)
          return {
            kind: "add-to-basket",
            value: {},
          }
        } else {
          const response = await props.submitMutation({
            input: {
              start: pendingReservation.start,
              end: pendingReservation.end,
              affiliateId: pendingReservation.affiliate.affiliateId,
              productVariantId: pendingReservation.productVariant.id,
              paymentMethod: pendingReservation.paymentMethod,
              accessories: pendingReservation.accessories.map(a => ({ name: a.name })),
              shippingAddress: pendingReservation.shippingAddress,
              profileId: pendingReservation.profileId,
            },
          })
          return {
            kind: "create-reservation",
            value: response,
          }
        }
      }

      // create new promise, as modal CB aren't promisified
      return new Promise((resolve, reject) => {
        if (!props.user.customer) {
          props.openModal(CreditCard, {
            type: "add",
            allowSkip: false,
            noClose: true,
            onSuccess: () => {
              return reserveProduct().then(resolve, reject);
            },
          })
        } else {
          reserveProduct().then(resolve, reject);
        }
      });
    },
    redirect: (_, res) => {
      if (res.kind === "add-to-basket") {
        return urls.shoppingBasket
      } else if (res.kind === "create-reservation") {
        return urls.reservation(res.value.id)
      } else {
        return (res: empty)
      }
    },
    onSuccess: props => res => {
      props.clear()

      if (res.kind === 'create-reservation') {
        props.openModal(ReservationSuccess, {})

        if (!hasPreviousReservation(props.data)) {
          trackReservation("First Booking", res.value, props.user)
        }

        trackReservation("Reservation Created", res.value, props.user)
      }
    },
    onError: props => error => {
      getErrorMessage(error)
    }
  }),
)

export default enhancer(CheckoutForm)


// WEBPACK FOOTER //
// ./src/pages/Checkout/CheckoutForm/index.js